import React, { useEffect, useState } from "react";

import "../../css/AppDialog.css";
import { QuestionStats } from "./FormDataTypes";
import { UseAPICaller } from "../../apiCaller";
import { InfoIcon } from "../AppDialog";

const URL_PATH :string = "185.7.62.58";

export interface AppDialogProps {
    isOpen: boolean;
    onDialogClose?: () => void;
  }

const SurveyDialogContent : React.FC<AppDialogProps> = ({isOpen, onDialogClose}) => {

    const [data, setData] = React.useState<Record<string, QuestionStats>>(null);
    const [surveyInfo, setSurveyInfo] = useState<{title:string, link:string, active:boolean, numResponses:number}>({title:"", link:"", active:false, numResponses:0});
    const {GetSurveyViewInfo, DeactivateSurvey, GetResponses, DeleteResource} = UseAPICaller();
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        if(!isOpen) return;
        const surveyId = +sessionStorage.getItem("surveyId");

        const SurveyViewInfo = async () =>{
            if (surveyId) 
            {
                const surveyInfo = await GetSurveyViewInfo(surveyId);
                setSurveyInfo(surveyInfo);
            }
        }

        const GetResponseData = async () =>{
            if (surveyId) 
            {
                const responses = await GetResponses(surveyId);

                if(!responses) return;
                
                const result: Record<string, QuestionStats> = {};

                const questionMap = responses.questions.reduce((acc, q) => {
                    acc[q.id] = q.text;
                    return acc;
                }, {} as Record<number, string>);

                for (const r of responses.responses) {
                    const text = questionMap[r.questionId];
                    if (!result[text]) {
                        result[text] = { values: [], average: 0 };
                    }
                    result[text].values.push(r.value);
                }

                for (const text in result) {
                    const arr = result[text].values;
                    const sum = arr.reduce((a, b) => a + b, 0);
                    result[text].average = sum / arr.length;
                }

                setData(result);
            }
        }

        SurveyViewInfo().then(GetResponseData);

    }, [isOpen]);


    const onLinkClicked = async () => {
        try 
        {
            await copyToClipboard(URL_PATH+surveyInfo.link);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (err) {
            console.error('Failed to copy!', err);
        }
    };

    async function copyToClipboard(text: string) {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
        } else {
            // fallback för HTTP
            const input = document.createElement('input');
            input.value = text;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
        }
    }

    const onEndSurvey = () => {
        if (window.confirm("Are you sure that you want to end this survey? \nThis will stop participants from responding from here on out.")) {
            const surveyId = sessionStorage.getItem("surveyId");
            if (surveyId) 
            {
                DeactivateSurvey(surveyId);
                setSurveyInfo(prev => ({...prev, active: false,}));
            }
        }
    };

    const OnDelete = async () => {
        if (window.confirm("Are you sure that you want to delete this survey? \nThis is not reversable.")) {
            const surveyId = +sessionStorage.getItem("surveyId");
            if (surveyId) 
            {
                if (await DeleteResource("surveys", surveyId))
                {
                    if(onDialogClose)
                        onDialogClose();
                }
            }
        }
    };

    return (
        <div className="dialog-body">
            <div className="dialog-column-left">
                <div style={{display:"flex", flexDirection:"column", gap:6}}>
                    <button className={`dialog-button ${surveyInfo.active ? "blue-button" : "inactive-button"}`} onClick={onEndSurvey}>{surveyInfo.active ? "End Survey" : "Survey Ended"}</button>
                </div>
                <div style={{marginTop:"auto", display:"flex", flexDirection:"column", gap:6}}>
                    <button className="dialog-button red-button" onClick={OnDelete}>Delete</button>
                </div>
            </div>
            <div style={{width:"100%", position:"relative"}}>
                <div style={{display:"flex", alignItems:"center"}}>
                    <h2>{surveyInfo.title}</h2>
                    <InfoIcon text={ surveyInfo.active ? 
                        "This survey is published. Below you will find the link that can be shared with participants. Click the link to copy it. Below the link you can see the current number of responses to this survey. Responses will show up further below under Questions/Answers/Average." : 
                        "This survey has ended. Below you can see the number of responses to this survey. Responses from the survey is shown further below under Questions/Answers/Average."}/>
                </div>
                { surveyInfo.active && (<h4>Click here to copy link: <span className="survey-link" onClick={onLinkClicked}>{URL_PATH+surveyInfo.link}</span></h4>)}
                {copied && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '-30px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            backgroundColor: 'green',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            opacity: 1,
                            pointerEvents: 'none',
                        }}
                    >
                    Copied to clipboard!
                    </span>
                )}
                <h4>Number of responses: {surveyInfo.numResponses}</h4>
                <div className="survey-view-column">
                    <div className="survey-view-row">
                        <span style={{color:"#003399", fontSize:"larger", fontWeight:"bold"}}>Question</span>
                        <span style={{color:"#003399", fontSize:"larger", fontWeight:"bold"}}>Answers <span style={{fontSize:"small"}}>{"(1=low, 5=high)"}</span></span>
                        <span style={{color:"#003399", fontSize:"larger", fontWeight:"bold"}}>Average</span>
                    </div>
                    <hr></hr>
                </div>
                <div className="survey-view-column">
                    {data !== null ? (Object.entries(data) as [string, QuestionStats][]).map(([question, stats]) => (
                        <div className="survey-view-row">
                            <span style={{fontWeight:"bold", textOverflow:"ellipsis"}}>{question}</span>
                            <td>{stats.values.join(", ")}</td>
                            <span>{stats.average.toFixed(2)}</span>
                        </div>
                    )) : <p>No responses made yet.</p>}
                </div>
            </div>
        </div>
    );
}

export default SurveyDialogContent;